import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { today, addDays } from "@/lib/perform";
import { ScoreRow } from "@/components/perform/WodParts";
import { Megaphone, Target, Heart, Pin, Plus, Trash2, Eye, Users } from "lucide-react";

const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";

function Challenges({ staff }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["challenges"], queryFn: async () => (await api.get("/api/challenges")).data });
  const [amt, setAmt] = useState({});
  const [f, setF] = useState(null);
  const log = async (c) => {
    const a = Number(amt[c.id]);
    if (!a) return toast.error(`How many ${c.unit}?`);
    try { await api.post(`/api/challenges/${c.id}/log`, { amount: a }); setAmt({ ...amt, [c.id]: "" }); toast.success(`+${a} ${c.unit}`); qc.invalidateQueries({ queryKey: ["challenges"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const create = async () => {
    try { await api.post("/api/challenges", { ...f, target: Number(f.target) }); setF(null); toast.success("Challenge launched"); qc.invalidateQueries({ queryKey: ["challenges"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="space-y-4" data-testid="challenges">
      {staff && (f ? (
        <div className="surface rounded-2xl p-5 space-y-2">
          <p className="font-heading font-bold">New club challenge</p>
          <input className={field} placeholder="Title, e.g. 10,000 burpees in October" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <input className={field} placeholder="What counts? (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input className={field} placeholder="Unit, e.g. burpees" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} />
            <input className={field} type="number" placeholder="Target each" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
            <input className={field} type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
            <input className={field} type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end"><button onClick={() => setF(null)} className="h-10 px-4 rounded-full border border-[#343A4A] text-sm">Cancel</button>
            <button onClick={create} className="h-10 px-5 rounded-full bg-acid text-black font-medium">Launch</button></div>
        </div>
      ) : (
        <button onClick={() => setF({ title: "", description: "", unit: "reps", target: 1000, start: today(), end: addDays(today(), 30) })}
          className="inline-flex items-center gap-2 rounded-full h-10 px-5 bg-acid text-black font-medium"><Plus className="w-4 h-4" /> New challenge</button>
      ))}
      {data.length === 0 && <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">No challenges yet. {staff ? "Launch one to get the squad moving." : "Your coach can launch club challenges here."}</p>}
      {data.map((c) => (
        <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl border border-[#1E222A] bg-[#0F1115] p-5">
          <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: "#FF5A5F" }} />
          <div className="relative flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="mono-label" style={{ color: c.status === "live" ? "#CCFF00" : "#8A91A6" }}>{c.status} · until {c.end}</p>
              <p className="font-heading font-black text-xl">{c.title}</p>
              {c.description && <p className="text-sm text-[#8A91A6]">{c.description}</p>}
            </div>
            <div className="text-right"><p className="font-heading font-black text-2xl">{Math.round(c.squad_total).toLocaleString()}</p><p className="mono-label">{c.unit} by the squad</p></div>
          </div>
          <div className="relative mt-4">
            <div className="flex justify-between text-sm"><span>You: {Math.round(c.mine?.total || 0)} / {c.target} {c.unit}</span><span className="text-acid">{c.mine?.pct || 0}%</span></div>
            <div className="h-3 rounded-full bg-[#1E222A] overflow-hidden mt-1">
              <motion.div className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#FF5A5F,#CCFF00)" }} initial={{ width: 0 }} animate={{ width: `${c.mine?.pct || 0}%` }} transition={{ duration: 0.8 }} />
            </div>
          </div>
          {c.status === "live" && (
            <div className="relative flex gap-2 mt-3">
              <input className={`${field} max-w-[10rem]`} type="number" min="1" placeholder={`+ ${c.unit}`} value={amt[c.id] || ""} onChange={(e) => setAmt({ ...amt, [c.id]: e.target.value })} data-testid="challenge-amount" />
              <button onClick={() => log(c)} className="h-10 px-5 rounded-full bg-acid text-black font-medium" data-testid="challenge-log">Add</button>
            </div>
          )}
          <div className="relative mt-4 grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {c.board.slice(0, 10).map((p) => (
              <div key={p.rank} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-1.5 ${p.mine ? "bg-acid/10 text-acid" : "bg-[#0B0D11]"}`}>
                <span className="w-5 text-[#8A91A6]">{p.rank}</span><span className="flex-1 truncate">{p.name}</span><span className="font-mono2">{Math.round(p.total)}</span>
              </div>
            ))}
          </div>
          {staff && <button onClick={async () => { if (window.confirm("Delete this challenge?")) { await api.delete(`/api/challenges/${c.id}`); qc.invalidateQueries({ queryKey: ["challenges"] }); } }}
            className="relative mt-3 text-xs text-[#8A91A6] hover:text-pulseError inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
        </motion.div>
      ))}
    </div>
  );
}

function News({ staff }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["posts"], queryFn: async () => (await api.get("/api/club/posts")).data });
  const [f, setF] = useState({ title: "", body: "", pinned: false });
  const [unread, setUnread] = useState({});
  const post = async () => {
    try { await api.post("/api/club/posts", f); setF({ title: "", body: "", pinned: false }); toast.success("Posted to your club"); qc.invalidateQueries({ queryKey: ["posts"] }); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const like = async (p) => { await api.post(`/api/club/posts/${p.id}/like`); qc.invalidateQueries({ queryKey: ["posts"] }); };
  const who = async (p) => { const { data } = await api.get(`/api/club/posts/${p.id}/unread`); setUnread({ ...unread, [p.id]: data }); };
  return (
    <div className="space-y-4" data-testid="news">
      {staff && (
        <div className="surface rounded-2xl p-5 space-y-2">
          <p className="font-heading font-bold flex items-center gap-2"><Megaphone className="w-4 h-4 text-acid" /> Post to your club</p>
          <input className={field} placeholder="Headline" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="post-title" />
          <textarea className="rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 py-2 text-sm w-full" rows={3} placeholder="Programming notes, events, comp sign-ups…" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
          <div className="flex items-center justify-between">
            <label className="text-sm text-[#B7BECB] flex items-center gap-2"><input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} /> Pin to top</label>
            <button onClick={post} className="h-10 px-5 rounded-full bg-acid text-black font-medium" data-testid="post-send">Post</button>
          </div>
        </div>
      )}
      {data.length === 0 && <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">No news yet.</p>}
      {data.map((p) => (
        <div key={p.id} className="surface rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="mono-label flex items-center gap-1.5">{p.pinned && <Pin className="w-3 h-3 text-acid" />}{p.author} · {new Date(p.created_at).toLocaleDateString()}</p>
              <p className="font-heading font-black text-lg mt-1">{p.title}</p>
            </div>
            {staff && <button onClick={async () => { if (window.confirm("Delete this post?")) { await api.delete(`/api/club/posts/${p.id}`); qc.invalidateQueries({ queryKey: ["posts"] }); } }} className="text-[#8A91A6] hover:text-pulseError" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
          </div>
          <p className="text-sm text-[#D7DCE5] whitespace-pre-line mt-2">{p.body}</p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <button onClick={() => like(p)} className={`inline-flex items-center gap-1.5 text-sm ${p.liked ? "text-[#FF5A5F]" : "text-[#8A91A6]"}`}><Heart className={`w-4 h-4 ${p.liked ? "fill-[#FF5A5F]" : ""}`} /> {p.like_count}</button>
            {staff && p.audience != null && (
              <button onClick={() => who(p)} className="inline-flex items-center gap-1.5 text-sm text-[#8A91A6] hover:text-white"><Eye className="w-4 h-4" /> read by {p.reads} of {p.audience}</button>
            )}
          </div>
          {unread[p.id] && <p className="text-xs text-[#8A91A6] mt-2">{unread[p.id].length ? `Not read yet: ${unread[p.id].map((u) => u.name).join(", ")}` : "Everyone has read it."}</p>}
        </div>
      ))}
    </div>
  );
}

function Feed() {
  const { data } = useQuery({ queryKey: ["community-feed"], queryFn: async () => (await api.get("/api/community/feed")).data });
  if (!data) return <p className="mono-label animate-pulse">loading…</p>;
  if (!data.scores.length) return <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">{data.scope === "club" ? "Scores from your squad appear here as they're logged." : "Join a club to see your squad's scores here. Your own scores show for now."}</p>;
  return (
    <div className="space-y-2" data-testid="feed">
      {data.scores.map((r) => (
        <div key={r.id}>
          <p className="mono-label mb-1">{r.wod_date} · {r.wod_title}</p>
          <ScoreRow r={r} compact />
        </div>
      ))}
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const staff = ["owner", "coach"].includes(user?.club_role) && user?.club_active;
  const inClub = !!user?.club_id;
  const [tab, setTab] = useState("feed");
  const tabs = [["feed", "Squad feed", Users], ["challenges", "Challenges", Target], ["news", "News", Megaphone]];
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} overline={user?.club_name || "community"} title="Your squad"
        subtitle="Every score your club logs, high fives, challenges and news from your coaches." />
      <div className="flex flex-wrap gap-2">
        {tabs.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 rounded-full px-4 h-10 text-sm border ${tab === k ? "bg-acid text-black border-acid" : "border-[#343A4A] text-[#B7BECB]"}`}><I className="w-4 h-4" /> {l}</button>
        ))}
      </div>
      {!inClub && tab !== "feed" ? (
        <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">Challenges and news come from your club. Enter your club code under Memberships to join.</p>
      ) : tab === "feed" ? <Feed /> : tab === "challenges" ? <Challenges staff={staff} /> : <News staff={staff} />}
    </div>
  );
}
